import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { GrupoModal } from "../../../components/modals/GrupoModal";
import GrupoTiposModal from "../../../components/modals/GrupoTiposModal";
import CamposTiposModal from "../../../components/modals/CamposTiposModal";
import GrupoConfiguracaoModal from "../../../components/modals/GrupoConfiguracaoModal";
import GrupoMembrosModal from "../../../components/modals/GrupoMembrosModal";
import { api } from "../../../services/api";
import { Grupo, GrupoTipo, CampoTipo } from "../../../types/grupos";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  ImageIcon,
  BarChart3,
  Zap,
  RefreshCw,
  Settings,
  Layers,
  Grid3x3,
  Palette,
  UserCheck,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export function Grupos() {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [gruposTipos, setGruposTipos] = useState<GrupoTipo[]>([]);
  const [camposTipos, setCamposTipos] = useState<CampoTipo[]>([]);
  const [gruposCargos, setGruposCargos] = useState<any[]>([]);
  const [filteredGrupos, setFilteredGrupos] = useState<Grupo[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGrupoTiposModalOpen, setIsGrupoTiposModalOpen] = useState(false);
  const [isCamposTiposModalOpen, setIsCamposTiposModalOpen] = useState(false);
  const [isGrupoConfigModalOpen, setIsGrupoConfigModalOpen] = useState(false);
  const [isGrupoMembrosModalOpen, setIsGrupoMembrosModalOpen] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState<Grupo | null>(null);
  const [configuringGrupo, setConfiguringGrupo] = useState<Grupo | null>(null);
  const [managingMembrosGrupo, setManagingMembrosGrupo] = useState<Grupo | null>(null);

  const loadGrupos = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await api.get("/grupos");
      setGrupos(response.data);
      setFilteredGrupos(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar grupos", { variant: "error" });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadGruposTipos = async () => {
    try {
      const response = await api.get("/grupos-tipos");
      setGruposTipos(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar tipos de grupos", { variant: "error" });
    }
  };

  const loadCamposTipos = async () => {
    try {
      const response = await api.get("/campos-tipos");
      setCamposTipos(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar tipos de campos", { variant: "error" });
    }
  };

  const loadGruposCargos = async () => {
    try {
      const response = await api.get("/grupos-cargos");
      setGruposCargos(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar cargos dos grupos", { variant: "error" });
    }
  };

  useEffect(() => {
    loadGrupos();
    loadGruposTipos();
    loadCamposTipos();
    loadGruposCargos();
  }, []);

  // Filtrar grupos quando o termo de busca mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredGrupos(grupos);
    } else {
      const filtered = grupos.filter(
        (grupo) =>
          grupo.grupo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          grupo.descricao.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredGrupos(filtered);
    }
  }, [searchTerm, grupos]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Busca já é feita em tempo real pelo useEffect
  };

  const openCreateModal = () => {
    setEditingGrupo(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingGrupo(null);
  };

  const handleSaveSuccess = () => {
    closeModal();
    loadGrupos();
  };

  const moveGrupo = async (idTipo: string, gruposDoTipo: Grupo[], index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === gruposDoTipo.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newGruposDoTipo = [...gruposDoTipo];
    const [removed] = newGruposDoTipo.splice(index, 1);
    newGruposDoTipo.splice(newIndex, 0, removed);

    // Atualizar estado local imediatamente para feedback instantâneo
    const gruposAtualizados = grupos.map(g => {
      const grupoEncontrado = newGruposDoTipo.find(ng => ng.id === g.id);
      if (grupoEncontrado) {
        return { ...g, ordem: newGruposDoTipo.indexOf(grupoEncontrado) + 1 };
      }
      return g;
    });

    setGrupos(gruposAtualizados);
    setFilteredGrupos(gruposAtualizados);

    try {
      // Preparar dados para o backend - apenas grupos do tipo específico
      const ordens = newGruposDoTipo.map((grupo, idx) => ({
        id: grupo.id,
        ordem: idx + 1
      }));

      await api.patch('/grupos/ordem', { ordens });
      enqueueSnackbar('Ordem atualizada com sucesso', { variant: 'success' });

      // Recarregar dados do servidor em background para garantir sincronização (sem loading)
      await loadGrupos(false);
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar ordem', { variant: 'error' });
      // Em caso de erro, recarregar para reverter
      await loadGrupos(false);
    }
  };

  const moveGrupoTipo = async (tiposAtuais: any[], index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === tiposAtuais.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newTipos = [...tiposAtuais];
    const [removed] = newTipos.splice(index, 1);
    newTipos.splice(newIndex, 0, removed);

    // Atualizar ordem localmente primeiro
    setGruposTipos(newTipos);

    try {
      // Preparar dados para o backend
      const ordens = newTipos.map((tipo, idx) => ({
        id: tipo.id,
        ordem: idx + 1
      }));

      await api.patch('/grupos-tipos/ordem', { ordens });
      enqueueSnackbar('Ordem dos tipos atualizada com sucesso', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar ordem dos tipos', { variant: 'error' });
      // Reverter mudanças em caso de erro
      loadGruposTipos();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Users className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Grupos
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os grupos militares do sistema
        </p>
      </div>

      {/* Painel de Administração */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-blue-700 flex items-center gap-2 text-sm">
              <Layers className="w-4 h-4" />
              Tipos de Grupos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Configure os tipos disponíveis (Companhia, Academia, etc.)
            </p>
            <Button
              onClick={() => setIsGrupoTiposModalOpen(true)}
              size="sm"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Settings className="w-3 h-3 mr-1" />
              Gerenciar Tipos
            </Button>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-purple-700 flex items-center gap-2 text-sm">
              <Grid3x3 className="w-4 h-4" />
              Tipos de Campos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Configure os tipos de campos dinâmicos disponíveis
            </p>
            <Button
              onClick={() => setIsCamposTiposModalOpen(true)}
              size="sm"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Settings className="w-3 h-3 mr-1" />
              Gerenciar Campos
            </Button>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-700 flex items-center gap-2 text-sm">
              <Palette className="w-4 h-4" />
              Configurações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Configure campos específicos para cada grupo
            </p>
            <Button
              onClick={() => setIsGrupoConfigModalOpen(true)}
              size="sm"
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Settings className="w-3 h-3 mr-1" />
              Configurar Grupos
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Grupos
              </CardTitle>
              <CardDescription>
                Digite o nome do grupo ou descrição para pesquisar ou deixe em branco para ver todos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Nome do Grupo</Label>
                  <InputText
                    id="search"
                    name="search"
                    title=""
                    placeholder="Digite o nome do grupo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={openCreateModal}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Grupo
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {filteredGrupos.length} grupo(s) encontrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredGrupos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum grupo encontrado
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Agrupar por tipo quando não há busca */}
                  {!searchTerm ? (
                    gruposTipos
                      .filter(tipo => filteredGrupos.some(g => g.id_tipo === tipo.id))
                      .map((tipo) => {
                        const gruposDoTipo = filteredGrupos.filter(g => g.id_tipo === tipo.id);
                        return (
                          <div key={tipo.id} className="space-y-3">
                            <div className="flex items-center gap-2 pb-2 border-b-2" style={{ borderColor: tipo.cor_identificacao }}>
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: tipo.cor_identificacao }}
                              />
                              <h3 className="font-semibold text-lg" style={{ color: tipo.cor_identificacao }}>
                                {tipo.nome}
                              </h3>
                              <Badge variant="outline" className="ml-2">
                                {gruposDoTipo.length} {gruposDoTipo.length === 1 ? 'grupo' : 'grupos'}
                              </Badge>
                            </div>
                            <div className="space-y-3">
                              {gruposDoTipo.map((grupo, index) => (
                                <div
                                  key={grupo.id}
                                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors ml-4"
                                >
                                  {/* Botões de reordenação */}
                                  <div className="flex flex-col gap-1 mr-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => moveGrupo(tipo.id, gruposDoTipo, index, 'up')}
                                      disabled={index === 0}
                                      className="h-6 w-6 p-0"
                                    >
                                      <ArrowUp className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => moveGrupo(tipo.id, gruposDoTipo, index, 'down')}
                                      disabled={index === gruposDoTipo.length - 1}
                                      className="h-6 w-6 p-0"
                                    >
                                      <ArrowDown className="h-3 w-3" />
                                    </Button>
                                  </div>

                                  <div className="flex items-center gap-4 flex-1">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden bg-muted">
                          {grupo.img ? (
                            <img
                              src={grupo.img}
                              alt={grupo.grupo}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Users className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{grupo.grupo}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={
                              grupo.status === "A"
                                ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                                : "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600"
                            }>
                              {grupo.status === "A" ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )}
                              {grupo.status === "A" ? "Ativo" : "Inativo"}
                            </Badge>
                            {grupo.tipo && (
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                                <Layers className="h-3 w-3 mr-1" />
                                {grupo.tipo.nome}
                              </Badge>
                            )}
                            <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                              <ImageIcon className="h-3 w-3 mr-1" />
                              Com Imagem
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 max-w-md">
                            {grupo.descricao}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setManagingMembrosGrupo(grupo);
                            setIsGrupoMembrosModalOpen(true);
                          }}
                          className="text-teal-600 border-teal-200 hover:bg-teal-50 dark:text-teal-400 dark:border-teal-700 dark:hover:bg-teal-900/30"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Membros
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/configuracoes/grupos/${grupo.id}/cargos`)}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 dark:text-indigo-400 dark:border-indigo-700 dark:hover:bg-indigo-900/30"
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Cargos
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/configuracoes/grupos/${grupo.id}/tipos-acoes`)}
                          className="text-green-600 border-green-200 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/30"
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Ações
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setConfiguringGrupo(grupo);
                            setIsGrupoConfigModalOpen(true);
                          }}
                          className="text-orange-600 border-orange-200 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-700 dark:hover:bg-orange-900/30"
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Configurar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingGrupo(grupo);
                            setIsModalOpen(true);
                          }}
                          className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      </div>
                    </div>
                              ))}
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    /* Listagem simples quando há busca */
                    <div className="space-y-3">
                      {filteredGrupos.map((grupo) => (
                        <div
                          key={grupo.id}
                          className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden bg-muted">
                              {grupo.img ? (
                                <img
                                  src={grupo.img}
                                  alt={grupo.grupo}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Users className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">{grupo.grupo}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={
                                  grupo.status === "A"
                                    ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                                    : "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600"
                                }>
                                  {grupo.status === "A" ? (
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                  ) : (
                                    <XCircle className="h-3 w-3 mr-1" />
                                  )}
                                  {grupo.status === "A" ? "Ativo" : "Inativo"}
                                </Badge>
                                {grupo.tipo && (
                                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                                    <Layers className="h-3 w-3 mr-1" />
                                    {grupo.tipo.nome}
                                  </Badge>
                                )}
                                <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                                  <ImageIcon className="h-3 w-3 mr-1" />
                                  Com Imagem
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                                {grupo.descricao}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setManagingMembrosGrupo(grupo);
                                setIsGrupoMembrosModalOpen(true);
                              }}
                              className="text-teal-600 border-teal-200 hover:bg-teal-50 dark:text-teal-400 dark:border-teal-700 dark:hover:bg-teal-900/30"
                            >
                              <Users className="w-4 h-4 mr-1" />
                              Membros
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/configuracoes/grupos/${grupo.id}/cargos`)}
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 dark:text-indigo-400 dark:border-indigo-700 dark:hover:bg-indigo-900/30"
                            >
                              <UserCheck className="w-4 h-4 mr-1" />
                              Cargos
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/configuracoes/grupos/${grupo.id}/tipos-acoes`)}
                              className="text-green-600 border-green-200 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/30"
                            >
                              <Zap className="w-4 h-4 mr-1" />
                              Ações
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setConfiguringGrupo(grupo);
                                setIsGrupoConfigModalOpen(true);
                              }}
                              className="text-purple-600 border-purple-200 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-700 dark:hover:bg-purple-900/30"
                            >
                              <Settings className="w-4 h-4 mr-1" />
                              Config
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingGrupo(grupo);
                                setIsModalOpen(true);
                              }}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/30"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Grupos</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {grupos.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Grupos Ativos</span>
                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                    {grupos.filter(grupo => grupo.status === "A").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tipos Cadastrados</span>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                    {gruposTipos.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Campos Disponíveis</span>
                  <Badge className="bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700">
                    {camposTipos.length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ações Rápidas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button
                  onClick={openCreateModal}
                  className="w-full justify-start bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Grupo
                </Button>
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    loadGrupos();
                  }}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar Lista
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <GrupoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        initialData={editingGrupo}
        onSave={handleSaveSuccess}
        gruposTipos={gruposTipos}
      />

      <GrupoTiposModal
        isOpen={isGrupoTiposModalOpen}
        onClose={() => setIsGrupoTiposModalOpen(false)}
        onUpdate={loadGruposTipos}
        gruposTipos={gruposTipos}
        camposTipos={camposTipos}
      />

      <CamposTiposModal
        isOpen={isCamposTiposModalOpen}
        onClose={() => setIsCamposTiposModalOpen(false)}
        onUpdate={loadCamposTipos}
        camposTipos={camposTipos}
      />

      {configuringGrupo && (
        <GrupoConfiguracaoModal
          isOpen={isGrupoConfigModalOpen}
          onClose={() => {
            setIsGrupoConfigModalOpen(false);
            setConfiguringGrupo(null);
          }}
          onUpdate={loadGrupos}
          grupo={configuringGrupo}
        />
      )}

      {managingMembrosGrupo && (
        <GrupoMembrosModal
          isOpen={isGrupoMembrosModalOpen}
          onClose={() => {
            setIsGrupoMembrosModalOpen(false);
            setManagingMembrosGrupo(null);
          }}
          onUpdate={loadGrupos}
          grupo={managingMembrosGrupo}
          gruposCargos={gruposCargos}
        />
      )}
    </div>
  );
}
